import { useCallback, useState } from 'react'
import Swal from 'sweetalert2'
import { toast } from 'react-toastify'
import { useProjectStore } from 'Store/Project'

interface UseReturnDefectiveOptions {
  projectId: string
  piles: any[]
  setPiles: (piles: any[]) => void
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function buildConfirmHtml(row: any): string {
  const pileTitle = row.pileTitle || '-'
  const itemCode = row.pileCode || '-'
  const serial = row.inventoryCodeD365 ?? row.inventoryCode ?? '-'

  const rowHtml = (label: string, value: string) =>
    `<div class="d-flex justify-content-between gap-4 py-2 border-bottom border-gray-200">
      <span class="text-muted text-nowrap">${label}</span>
      <span class="fw-bold text-gray-800 text-end">${escapeHtml(value)}</span>
    </div>`

  return `
    <p class="text-gray-700 mb-4">ยืนยันส่งคืนเสานี้เป็นเสาเสียหรือไม่?</p>
    <div class="text-start bg-light rounded p-4 mb-1">
      ${rowHtml('ชื่อเสา', pileTitle)}
      ${rowHtml('Item Code', itemCode)}
      ${rowHtml('รหัสบาร์โค้ด', serial)}
    </div>
  `
}

export function useReturnDefective({ projectId, piles, setPiles }: UseReturnDefectiveOptions) {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const isEligible = useCallback((row: any) => row.usageStatus !== 'returned', [])

  const askReturnDefective = useCallback(
    (row: any) => {
      if (!isEligible(row) || isSubmitting) return

      Swal.fire({
        title: 'ส่งคืนเสาเสีย?',
        html: buildConfirmHtml(row),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'ยืนยัน',
        cancelButtonText: 'ยกเลิก',
        width: 480,
      }).then(async result => {
        if (!result.isConfirmed) return

        setIsSubmitting(true)
        try {
          const { success, data } = await useProjectStore
            .getState()
            .returnPile(parseInt(projectId, 10), Number(row.id))

          if (!success) {
            toast.error(data || 'ส่งคืนเสาเสียไม่สำเร็จ')
            return
          }

          setPiles(
            piles.map(pile =>
              String(pile.id) === String(data.inventoryId ?? row.id)
                ? { ...pile, ...data }
                : pile
            )
          )

          await useProjectStore.getState().getOne(parseInt(projectId, 10))
          toast.success('ส่งคืนเสาเสียสำเร็จ')
        } catch {
          toast.error('ส่งคืนเสาเสียไม่สำเร็จ')
        } finally {
          setIsSubmitting(false)
        }
      })
    },
    [isEligible, isSubmitting, projectId, piles, setPiles]
  )

  return {
    isEligible,
    isSubmitting,
    askReturnDefective,
  }
}
